import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowRight,
  Bell,
  BookOpenText,
  Cake,
  CalendarBlank,
  CaretRight,
  ClipboardText,
  Confetti,
  Folders,
  GraduationCap,
  Lightning,
  Link as LinkIcon,
  Megaphone,
  Newspaper,
  Phone,
  Sparkle,
  UsersThree,
} from "@phosphor-icons/react";
import { DASHBOARD } from "@/constants/testIds";

import { API } from "../lib/api";
const FORM_ROLES = ["gerente", "autor_area", "administrador_total", "admin", "area_manager", "coordinator", "regional_manager", "store_manager"];

const DEMO_DATA = {
  urgent_news: {
    title: "Atualização do sistema interno neste sábado",
    content: "O portal ficará indisponível das 22h às 23h para manutenção programada.",
  },
  bulletins: [
    {
      item_id: "news-1",
      category: "Pessoas",
      title: "Programa de desenvolvimento abre nova turma",
      content: "Inscrições abertas para colaboradores até sexta-feira.",
      tone: "blue",
    },
    {
      item_id: "news-2",
      category: "Operações",
      title: "Novo procedimento para solicitação de materiais",
      content: "Consulte o passo a passo atualizado na área de procedimentos.",
      tone: "cyan",
    },
    {
      item_id: "news-3",
      category: "Bem-estar",
      title: "Semana da Saúde começa na próxima segunda",
      content: "Confira a programação preparada para todas as unidades.",
      tone: "yellow",
    },
  ],
  documents: [
    {
      item_id: "documento-1",
      title: "Regulamento interno",
      content: "Documento de referência para consulta.",
      folder: "Geral",
      category: "Geral",
      url: "#",
    },
  ],
  announcements: [
    { item_id: "aviso-1", title: "Fechamento da folha no dia 10", department: "Recursos Humanos", priority: "high" },
    { item_id: "aviso-2", title: "Atualização obrigatória dos dados cadastrais", department: "Administração" },
    { item_id: "aviso-3", title: "Pesquisa interna disponível até sexta", department: "Comunicação" },
  ],
  upcoming_birthdays: [
    { user_id: "1", name: "Ana Martins", department: "Comercial", days_until_birthday: 0, birthday_formatted: "Hoje" },
    { user_id: "2", name: "Carlos Souza", department: "Logística", days_until_birthday: 2, birthday_formatted: "10 jul" },
    { user_id: "3", name: "Juliana Lima", department: "Financeiro", days_until_birthday: 4, birthday_formatted: "12 jul" },
  ],
  campaigns: [
    {
      item_id: "campanha-1",
      title: "Juntos fazemos a diferença",
      content: "Participe da campanha de arrecadação de agasalhos nas unidades.",
      end_date: "18 de julho",
    },
  ],
  trainings: [
    { item_id: "treino-1", title: "Segurança da informação", category: "Obrigatório · 25 min" },
    { item_id: "treino-2", title: "Atendimento com excelência", category: "Desenvolvimento · 40 min" },
  ],
  useful_links: [
    { item_id: "link-1", title: "Portal RH", url: "#" },
    { item_id: "link-2", title: "Controle de ponto", url: "#" },
    { item_id: "link-3", title: "Suporte de TI", url: "#" },
    { item_id: "link-4", title: "Google Drive", url: "#" },
  ],
};

const quickActions = [
  { label: "Procedimentos", description: "Normas e orientações", icon: BookOpenText, path: "/procedimentos", color: "bg-[#eef1ff]" },
  { label: "Documentos", description: "Arquivos e regulamentos", icon: Folders, path: "/documentos", color: "bg-[#f4f7ff]" },
  { label: "Formulários", description: "Modelos autorizados", icon: ClipboardText, path: "/formularios", color: "bg-[#e9f8fb]" },
  { label: "Contatos", description: "Encontre uma pessoa", icon: Phone, path: "/contatos", color: "bg-[#fff7d6]" },
  { label: "Organograma", description: "Áreas e lideranças", icon: UsersThree, path: "/organograma", color: "bg-[#f2ecff]" },
];

const thumbnailStyles = {
  blue: "from-[#dfe5ff] via-[#eef1ff] to-white",
  cyan: "from-[#d8f4f8] via-[#eaf9fb] to-white",
  yellow: "from-[#ffed9c] via-[#fff6ce] to-white",
};

function stripHtml(html = "") {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = String(html);
  return div.textContent || div.innerText || "";
}

function SectionTitle({ icon: Icon, title, subtitle, path, action = "Ver todos" }) {
  return (
    <div className="mb-5 flex items-start justify-between gap-4">
      <div className="flex min-w-0 items-start gap-3">
        <span className="soft-icon h-10 w-10 shrink-0">
          <Icon className="h-5 w-5" weight="regular" />
        </span>
        <div>
          <h2 className="text-base font-bold text-slate-950">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs leading-5 text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {path && (
        <Link to={path} className="flex min-h-11 shrink-0 items-center gap-1 rounded-xl px-2 text-xs font-bold text-[#2346ff] hover:bg-[#eef1ff]">
          {action}<CaretRight className="h-3.5 w-3.5" weight="bold" />
        </Link>
      )}
    </div>
  );
}

function NewsCard({ item }) {
  const cleanContent = stripHtml(item.content);

  return (
    <Link to="/boletim" className="group min-w-0 rounded-2xl border border-slate-100 bg-white p-2.5 transition hover:border-[#cbd4ff] hover:shadow-[0_12px_30px_rgba(35,70,255,0.09)]">
      <div className={`relative aspect-[16/9] overflow-hidden rounded-xl bg-gradient-to-br ${thumbnailStyles[item.tone] || thumbnailStyles.blue}`}>
        <div className="absolute -right-5 -top-6 h-24 w-24 rounded-full border-[18px] border-white/45" />
        <div className="absolute bottom-3 left-3 grid h-10 w-10 place-items-center rounded-xl bg-white/90 text-[#2346ff] shadow-sm">
          {item.tone === "yellow"
            ? <Confetti className="h-5 w-5" weight="regular" />
            : item.tone === "cyan"
              ? <ClipboardText className="h-5 w-5" weight="regular" />
              : <GraduationCap className="h-5 w-5" weight="regular" />}
        </div>
      </div>
      <div className="px-1 pb-2 pt-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#2346ff]">{item.category}</p>
        <h3 className="mt-1.5 line-clamp-2 text-sm font-bold leading-5 text-slate-900 group-hover:text-[#2346ff]">{item.title}</h3>
        <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">{cleanContent || "Clique para ler o boletim."}</p>
      </div>
    </Link>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="grid min-h-28 place-items-center rounded-xl bg-slate-50 p-4 text-center">
      <div>
        <Icon className="mx-auto h-6 w-6 text-[#2346ff]" weight="light" />
        <p className="mt-2 text-xs text-slate-500">{text}</p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, demoMode } = useAuth();
  const [data, setData] = useState(demoMode ? DEMO_DATA : null);
  const [loading, setLoading] = useState(!demoMode);
  const [error, setError] = useState("");

  useEffect(() => {
    if (demoMode) return;
    axios.get(`${API}/dashboard/stats`, { withCredentials: true })
      .then(response => {
        setData(response.data);
        setError("");
      })
      .catch(() => {
        setData(null);
        setError("Nao foi possivel carregar os dados da intranet. Verifique se o backend esta ativo.");
      })
      .finally(() => setLoading(false));
  }, [demoMode]);

  const today = new Date().toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  if (loading) {
    return (
      <div className="mx-auto max-w-[1440px] space-y-5 p-4 sm:p-6 lg:p-8">
        <div className="h-24 animate-pulse rounded-3xl bg-slate-200" />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map(item => <div key={item} className="h-28 animate-pulse rounded-2xl bg-slate-200" />)}
        </div>
      </div>
    );
  }

  if (error && !demoMode) {
    return (
      <div className="mx-auto max-w-[1440px] p-4 sm:p-6 lg:p-8">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
          {error}
        </div>
      </div>
    );
  }

  const content = data || DEMO_DATA;
  const firstName = user?.name?.split(" ")[0] || "Marina";
  const visibleQuickActions = quickActions.filter(item => item.path !== "/formularios" || FORM_ROLES.includes(user?.role));

  return (
    <div data-testid={DASHBOARD.container} className="mx-auto w-full max-w-[1440px] px-4 py-5 sm:px-6 sm:py-7 xl:px-8">
      <section className="cute-surface relative mb-5 overflow-hidden rounded-[28px] p-5 sm:p-7">
        <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-[#2346ff]/[0.06]" />
        <div className="relative flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <span className="grid h-8 w-8 place-items-center rounded-xl bg-[#fff4bf] text-[#2346ff]">
                <Sparkle className="h-4 w-4" weight="fill" />
              </span>
              <p className="text-xs font-bold uppercase tracking-[0.13em] text-[#2346ff]">Seu espaço Schumann</p>
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">Olá, {firstName}!</h1>
            <p className="mt-1 text-sm capitalize text-slate-500">{today}</p>
          </div>

          <Link to="/ultima-hora" className="group flex w-full max-w-xl items-start gap-3 rounded-2xl border border-[#d5dcff] bg-white/90 p-4 shadow-sm lg:w-auto">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#eef1ff] text-[#2346ff]">
              <Lightning className="h-5 w-5" weight="regular" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#2346ff]">Informação de última hora</p>
              <p className="mt-1 line-clamp-1 text-sm font-bold text-slate-900">{content.urgent_news?.title}</p>
              <p className="mt-0.5 line-clamp-1 text-xs text-slate-500">{content.urgent_news?.content}</p>
            </div>
            <ArrowRight className="mt-2 h-4 w-4 shrink-0 text-[#2346ff] transition group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
        {visibleQuickActions.map(({ label, description, icon: Icon, path, color }) => (
          <Link key={path} to={path} className="bento-card group flex min-h-[104px] items-center gap-3 p-3.5 sm:p-4">
            <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl text-[#2346ff] ${color}`}>
              <Icon className="h-5 w-5" weight="regular" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-900 group-hover:text-[#2346ff]">{label}</p>
              <p className="mt-1 hidden text-xs text-slate-500 sm:block">{description}</p>
            </div>
          </Link>
        ))}
      </section>

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1.65fr)_minmax(300px,0.65fr)]">
        <div className="space-y-5">
          <section className="bento-card p-4 sm:p-5">
            <SectionTitle icon={Newspaper} title="Novidades internas" subtitle="Informações importantes para o seu dia a dia" path="/boletim" />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {content.bulletins?.slice(0, 3).map(item => <NewsCard key={item.item_id} item={item} />)}
            </div>
          </section>

          <section className="bento-card p-4 sm:p-5">
            <SectionTitle icon={Folders} title="Documentos recentes" subtitle="Arquivos e regulamentos publicados pelas áreas" path="/documentos" action="Ver documentos" />
            {content.documents?.length ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {content.documents.slice(0, 3).map(item => {
                  const url = item.url || item.file_url;
                  const folder = item.folder || item.category || "Geral";
                  const card = (
                    <div className="flex min-h-24 items-start gap-3 rounded-2xl border border-slate-100 bg-white p-4 transition hover:border-[#cbd4ff] hover:bg-[#f8f9ff]">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#eef1ff] text-[#2346ff]">
                        <Folders className="h-5 w-5" weight="regular" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-1 text-sm font-bold text-slate-900">{item.title}</p>
                        <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">{item.content || "Documento publicado para consulta."}</p>
                        <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#2346ff]">{folder}</p>
                      </div>
                      <CaretRight className="mt-1 h-4 w-4 shrink-0 text-[#2346ff]" />
                    </div>
                  );
                  return url ? (
                    <a key={item.item_id} href={url} target="_blank" rel="noreferrer">{card}</a>
                  ) : (
                    <Link key={item.item_id} to="/documentos">{card}</Link>
                  );
                })}
              </div>
            ) : (
              <EmptyState icon={Folders} text="Nenhum documento publicado" />
            )}
          </section>

          <div className="grid gap-5 lg:grid-cols-2">
            <section className="bento-card p-4 sm:p-5">
              <SectionTitle icon={Megaphone} title="Campanhas ativas" path="/campanhas" action="Ver campanhas" />
              {content.campaigns?.length ? content.campaigns.slice(0, 1).map(item => (
                <Link key={item.item_id} to="/campanhas" className="block rounded-2xl bg-gradient-to-br from-[#2346ff] to-[#526bff] p-5 text-white">
                  <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/70">Participe</p>
                  <h3 className="mt-2 text-base font-bold">{item.title}</h3>
                  <p className="mt-2 text-xs leading-5 text-white/75">{item.content}</p>
                  <p className="mt-4 flex items-center gap-1.5 text-xs font-bold">
                    <CalendarBlank className="h-4 w-4" /> Até {item.end_date}
                  </p>
                </Link>
              )) : <EmptyState icon={Megaphone} text="Nenhuma campanha ativa" />}
            </section>

            <section className="bento-card p-4 sm:p-5">
              <SectionTitle icon={GraduationCap} title="Treinamentos" path="/treinamentos" />
              <div className="space-y-2">
                {content.trainings?.length ? content.trainings.slice(0, 3).map((item, index) => (
                  <Link key={item.item_id} to="/treinamentos" className="flex min-h-14 items-center gap-3 rounded-xl border border-slate-100 p-3 hover:border-[#cbd4ff] hover:bg-[#f8f9ff]">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#eef1ff] text-xs font-bold text-[#2346ff]">{index + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-slate-800">{item.title}</p>
                      <p className="mt-0.5 text-[11px] text-slate-500">{item.category}</p>
                    </div>
                    <CaretRight className="h-4 w-4 text-[#2346ff]" />
                  </Link>
                )) : <EmptyState icon={GraduationCap} text="Nenhum treinamento disponível" />}
              </div>
            </section>
          </div>
        </div>

        <aside className="grid gap-5 sm:grid-cols-2 xl:grid-cols-1">
          <section data-testid={DASHBOARD.announcementsWidget} className="bento-card p-4 sm:p-5">
            <SectionTitle icon={Bell} title="Mural de avisos" path="/mural" action="Abrir mural" />
            <div className="space-y-2">
              {content.announcements?.slice(0, 3).map(item => (
                <Link key={item.item_id} to="/mural" className="flex gap-3 rounded-xl border border-slate-100 p-3 hover:bg-slate-50">
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${item.priority === "high" ? "bg-rose-500" : "bg-[#2346ff]"}`} />
                  <div>
                    <p className="text-xs font-bold leading-5 text-slate-800">{item.title}</p>
                    <p className="mt-0.5 text-[10px] text-slate-500">{item.department}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <section data-testid={DASHBOARD.birthdaysWidget} className="bento-card p-4 sm:p-5">
            <SectionTitle icon={Cake} title="Aniversariantes" path="/aniversariantes" />
            <div className="space-y-2">
              {content.upcoming_birthdays?.slice(0, 3).map((person, index) => (
                <div key={person.user_id} className="flex min-h-14 items-center gap-3 rounded-xl p-2 hover:bg-slate-50">
                  <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-2xl text-xs font-bold ${index === 0 ? "bg-[#fff1a8] text-slate-800" : "bg-[#eef1ff] text-[#2346ff]"}`}>
                    {person.name.split(" ").map(part => part[0]).slice(0, 2).join("")}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-bold text-slate-800">{person.name}</p>
                    <p className="mt-0.5 truncate text-[10px] text-slate-500">{person.department}</p>
                  </div>
                  <span className="text-[10px] font-bold text-[#2346ff]">{person.birthday_formatted}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="bento-card p-4 sm:col-span-2 sm:p-5 xl:col-span-1">
            <SectionTitle icon={LinkIcon} title="Links úteis" path="/links-uteis" action="Ver todos" />
            <div className="grid grid-cols-2 gap-2">
              {content.useful_links?.slice(0, 4).map(item => (
                <a key={item.item_id} href={item.url} className="flex min-h-11 items-center justify-between rounded-xl bg-slate-50 px-3 text-xs font-bold text-slate-700 hover:bg-[#eef1ff] hover:text-[#2346ff]">
                  <span className="truncate">{item.title}</span><CaretRight className="h-3 w-3 shrink-0" />
                </a>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
